import {Injectable, OnDestroy} from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import {BehaviorSubject, EMPTY, Observable, Subscription, throwError} from 'rxjs';
import {catchError, filter, take} from 'rxjs/operators';
import {AuthFacade} from '../facade/auth.facade';
import {Authentication} from '../models/authentication.model';
import {switchMap} from 'rxjs/operators';
import {AuthApi} from '../api/auth.api';
import {Router} from '@angular/router';
import { DataSourceUploadProgressService } from '../../data-source/services/data-source-upload-progress.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor, OnDestroy {
    private refreshTokenSubject = new BehaviorSubject<string>(null);
    private user: Partial<Authentication>;
    private subscription: Subscription;

    constructor(private authFacade: AuthFacade, private authApi: AuthApi, private router: Router, private progressService: DataSourceUploadProgressService) {
        this.subscription = this.authFacade.getUserAuthentication$().subscribe(userTemp => {
            if (userTemp) {
                this.user = userTemp;
            }
        });
    }
    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        let modifiedReq = request;
        
        if (request.url.includes('auth/login')) {
            return next.handle(request);
        }

        if (modifiedReq.headers.get('skip')) {
            modifiedReq.headers.delete('skip');
        } else {
            this.user = JSON.parse(this.authFacade.getLocalStorageData('userData'));
            if (this.user) {
                if (this.user.tokenIsRefreshing) {
                    this.refreshTokenSubject.next(null);
                    return this.queueRequest(modifiedReq, next);
                } else {
                    this.refreshTokenSubject.next(this.user.accessToken);
                }
                modifiedReq = this.addTokenHeader(modifiedReq, this.user.accessToken);
            }
        }
        return this.handleRequest(request, modifiedReq, next);
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private handleRequest(request: HttpRequest<unknown>, modifiedReq: HttpRequest<unknown>,
                          next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(modifiedReq).pipe(

            catchError((error) => {

                if (error instanceof HttpErrorResponse) {
                  if (error.error && error.error.statusCode === 7) {
                        return this.handleRefreshError(request, next);
                    }
                    if (!this.router.url.includes('auth/login')) {
                        if (error.status === 401) {
                          if (!this.user.tokenIsRefreshing) {
                            this.authFacade.resetStates();
                            if(error.error && error.error.statusCode && error.error.statusCode === 9){
                              this.router.navigate(['/ip-error'], {
                                queryParams: {
                                  ip: error.error.message.split(' ')[1],
                                },
                              }).then( ()=> {
                                this.progressService.reset()
                              });
                            }else{
                              this.progressService.reset()
                              this.router.navigate(['auth/login']).then( ()=>{
                              this.progressService.reset()
                              });
                            }
                            return EMPTY;                          }
                    }
                        if (error.status === 403) {
                          if(request.url.indexOf('/api/v1/File/Download?path=/api/v1/File/Download?path') === -1){
                            this.router.navigate(['/403']);
                          }
                          return EMPTY;                          }
                    }
                    if (error.status === 500) {
                        this.router.navigate(['/500']);
                        return EMPTY;                      }
                    if (error.status === 502) {
                        this.router.navigate(['/502']);
                        return EMPTY;                      }
                }
                return throwError(() => error);

            }),
        );
    }

    private handleRefreshError(
        request: HttpRequest<unknown>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        if (!this.user.tokenIsRefreshing) {
            this.user.tokenIsRefreshing = true;
            this.refreshTokenSubject.next(null);
            this.authFacade.updateAuthState(this.user);

            const body = {
                token: this.user.accessToken,
                refreshToken: this.user.refreshToken,
            };
            return this.authApi.refreshToken(body).pipe(
                catchError((error) => {
                        this.authFacade.resetStates();
                        this.router.navigate(['auth/login']).then ( ()=>{
                            this.progressService.reset()
                        });
                        return throwError(error);
                    },
                ),
                switchMap((res: any) => {
                    const data: Partial<Authentication> = {
                        accessToken: res.access_token,
                        refreshToken: res.refresh_token,
                        expiresIn: res.expires_in,
                        tokenType: res.token_type,
                        tokenIsRefreshing: false,
                    };
                    this.user.refreshToken = res.refresh_token;
                    this.authFacade.refreshToken(data);
                    this.refreshTokenSubject.next(data.accessToken);
                    return next.handle(this.addTokenHeader(request, data.accessToken));
                }),
            );
        } else {
            return this.queueRequest(request, next);
        }
    }

    private addTokenHeader(request: HttpRequest<any>, token: string): any {
        if(request.url.indexOf('User/CheckCaptchaRequired')!==-1){
          return request
        }
        return request.clone({
            headers: new HttpHeaders({
                authorization: 'Bearer ' + token,
            }),
        });
    }

    private queueRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return this.refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => next.handle(this.addTokenHeader(request, token))),
        );
    }
}
